import React from 'react';
import _merge from 'lodash/merge';
import { getContext } from 'common';
import {
  BODY,
  TITLE_16,
  TITLE_24
} from '@nokia-csf-uxr/freeform-design-tokens/tokens/typography';
import {
  FONT_WEIGHT_LIGHT,
  FONT_WEIGHT_REGULAR,
  FONT_WEIGHT_BOLD
} from '@nokia-csf-uxr/freeform-design-tokens/tokens/fontWeight';
import {
  LINE_HEIGHT_NONE
} from '@nokia-csf-uxr/freeform-design-tokens/tokens/lineHeight';

import Styled from './styles';


const NORMAL_STATE_TYPOGRAPHY = {
  medium: {
    singleChar: {
      fontSize: '2.1875rem',
      fontWeight: FONT_WEIGHT_LIGHT,
      lineHeight: LINE_HEIGHT_NONE
    },
    multipleChars: {
      ...TITLE_16,
      fontWeight: FONT_WEIGHT_REGULAR,
      lineHeight: LINE_HEIGHT_NONE,
    }
  },
  small: {
    singleChar: {
      ...TITLE_24,
      fontWeight: FONT_WEIGHT_LIGHT,
      lineHeight: LINE_HEIGHT_NONE
    },
    multipleChars: {
      ...BODY,
      lineHeight: LINE_HEIGHT_NONE
    }
  },
};

const FOCUSED_STATE_TYPOGRAPHY = {
  medium: {
    singleChar: {
      fontSize: '2.1875rem',
      fontWeight: FONT_WEIGHT_BOLD,
      lineHeight: LINE_HEIGHT_NONE,
    },
    multipleChars: {
      ...TITLE_16,
      fontWeight: FONT_WEIGHT_BOLD,
      lineHeight: LINE_HEIGHT_NONE,
    }
  },
  small: {
    singleChar: {
      ...TITLE_24,
      fontWeight: FONT_WEIGHT_BOLD,
      lineHeight: LINE_HEIGHT_NONE
    },
    multipleChars: {
      ...BODY,
      fontWeight: FONT_WEIGHT_BOLD,
      lineHeight: LINE_HEIGHT_NONE
    }
  },
};

export interface SearchwChipsOperatorProps extends React.ComponentPropsWithRef<'div'> {
  /** If true, SearchwChipsOperator will be rendered in focused state. */
  focused?: boolean;
  /** If true, SearchwChipsOperator will be selected. */
  selected?: boolean;
  /** The label of the SearchwChipsOperator. */
  label?: string;
  /** The size of the component. */
  size?: 'small' | 'medium';
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** To customize the wrapper style. */
  style?: React.CSSProperties
}

/** `SearchwChipsOperator` renders expression builders operators such as, "AND", "OR" and brackets. */
const SearchwChipsOperator = React.forwardRef<HTMLElement, SearchwChipsOperatorProps>((props: SearchwChipsOperatorProps, ref) => getContext(({ RTL }) => {
  const {
    theme,
    selected,
    label,
    size,
    style,
    focused,
    ...otherProps
  } = props;
  const isSingleChar = label && label.length === 1;
  const resolvedStyle = _merge(
    {},
    focused
      ? FOCUSED_STATE_TYPOGRAPHY[size][isSingleChar ? 'singleChar' : 'multipleChars']
      : NORMAL_STATE_TYPOGRAPHY[size][isSingleChar ? 'singleChar' : 'multipleChars'],
    style
  );
  return (
    <Styled.SearchwChipsOperator
      selected={selected}
      $theme={theme}
      ref={ref}
      RTL={RTL}
      style={resolvedStyle}
      {...otherProps}
    >
      {label}
    </Styled.SearchwChipsOperator>
  );
}));

SearchwChipsOperator.displayName = 'SearchwChipsOperator';

SearchwChipsOperator.defaultProps = {
  label: undefined,
  selected: false,
  size: 'medium',
  focused: false,
  theme: {},
  style: {}
};

export default SearchwChipsOperator;
